export class EpubReadAhead {
  constructor({
    book,
    window: windowObject = globalThis.window,
    document: documentObject = globalThis.document,
    navigator: navigatorObject = globalThis.navigator,
    logger = console
  }) {
    this.book = book
    this.window = windowObject
    this.document = documentObject
    this.navigator = navigatorObject
    this.logger = logger
    this.currentSection = null
    this.task = null
    this.section = null
    this.loadingSection = null

    this.connection = this.navigator.connection
    this.connectionChangeHandler = this.refresh.bind(this)
    this.connection?.addEventListener?.("change", this.connectionChangeHandler)
  }

  rendered(section) {
    this.currentSection = section
    if (this.section === section) this.section = null
    this.schedule(section)
  }

  refresh() {
    if (!this.allowed()) {
      this.cancelTask()
      return
    }

    if (this.currentSection) this.schedule(this.currentSection)
  }

  disconnect() {
    this.cancelTask()
    this.connection?.removeEventListener?.("change", this.connectionChangeHandler)
  }

  allowed() {
    const connection = this.navigator.connection
    const slowConnection = ["slow-2g", "2g"].includes(connection?.effectiveType)

    return this.navigator.onLine !== false &&
      this.document.visibilityState === "visible" &&
      !connection?.saveData &&
      !slowConnection
  }

  schedule(section) {
    this.cancelTask()

    const nextSection = section?.next?.()
    this.releaseUnusedSection(section, nextSection)
    if (!nextSection || !this.allowed()) return
    if (nextSection.contents || this.loadingSection === nextSection) return

    const preload = () => {
      this.task = null
      return this.preload(section, nextSection)
    }

    if (this.window.requestIdleCallback) {
      this.task = {
        type: "idle",
        id: this.window.requestIdleCallback(preload, { timeout: 1500 })
      }
    } else {
      this.task = { type: "timeout", id: this.window.setTimeout(preload, 250) }
    }
  }

  async preload(currentSection, nextSection) {
    if (this.currentSection !== currentSection || !this.allowed()) return

    this.loadingSection = nextSection
    try {
      await nextSection.load(this.book.load.bind(this.book))

      if (this.currentSection === currentSection) {
        this.section = nextSection
      } else if (this.currentSection !== nextSection) {
        nextSection.unload()
      }
    } catch (error) {
      this.logger.debug("Unable to preload the next EPUB section", error)
    } finally {
      if (this.loadingSection === nextSection) this.loadingSection = null
    }
  }

  releaseUnusedSection(currentSection, nextSection) {
    if (!this.section) return
    if (this.section === currentSection) {
      this.section = null
      return
    }
    if (this.section === nextSection) return

    this.section.unload()
    this.section = null
  }

  cancelTask() {
    if (!this.task) return

    if (this.task.type === "idle") {
      this.window.cancelIdleCallback(this.task.id)
    } else {
      this.window.clearTimeout(this.task.id)
    }
    this.task = null
  }
}
